/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  CssBaseline,
  Container,
  IconButton,
  Typography,
  Grid,
  useMediaQuery,
  CircularProgress,
} from "@material-ui/core";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import Add from "@material-ui/icons/Add";
import { Searchbar, SortButton } from "../components-molecules";
import { IFilter } from "../components-molecules/FilterButton";
import { ButtonPrimary } from "../components-atoms";
import { CollectionOrdersList } from "../components-organisms/CollectionOrdersList";
import { CustomTabs } from "../components-molecules/Tabs";
import { CustomTab } from "../components-molecules/Tab";
import { ASC_SORT, SortOption, SortType } from "../utils/sortType";
import { collectionOrdersStyles } from "../utils/styles/collectionOrdersStyles";
import {
  CollectOrderListModel,
  getDeliveryStatusTypes,
  getMerchantFilters,
} from "../redux/actions/collectOrders";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCollectionList,
  GetCollectionOrderList,
} from "../redux/actions/collectionOrderList";
import { getCollectOrderTabUrl } from "../utils/getCollectOrderTab";
import {
  FilterButtonWithDate,
  IFilterItem,
} from "../components-molecules/FilterButtonWithDate";
import { CollectionOrdersFilterOptions } from "../redux/reducers/collectOrdersFilters";
import {
  ButtonAreaResponsive,
  HeaderWithButtonsResponsive,
} from "../components-molecules/HeaderWithButtonsResponsive";
import { useHistory, useLocation } from "react-router-dom";
import { ICollectOrderItemSource } from "../components-molecules/CollectionOrderItem";

const DELIVERY_ORDERS_TABS = ["Open orders", "In Transit", "Failed", "Archive"];
const DEBOUNCE_TIMEOUT = 500;
const pageSize = 10;
export interface IFilterOptions {
  [key: string]: IFilterItem[];
}

export function CollectionOrders() {
  const dispatch = useDispatch();
  const role = useSelector((state: any) => state.login.role);
  const isTKAdmin = role === "Admin";
  const classes = collectionOrdersStyles();
  const mobileView = useMediaQuery("(max-width: 899px)", { noSsr: true });
  const [filterOptions, setFilterOptions] = useState<IFilterOptions>(
    CollectionOrdersFilterOptions
  );

  const [orders, loading, totalCount]: [
    CollectOrderListModel[],
    boolean,
    number
  ] = useSelector((state: any) => [
    state.collectionOrderList.data,
    state.collectionOrderList.loading,
    state.collectionOrderList.totalCount,
  ]);
  const userData = useSelector((state: any) => state.login);

  const location = useLocation();
  const history = useHistory();

  // URL Filters
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = useMemo(() => Number(urlParams.get('page')), [location.search]) || 1;
  const query = useMemo(() => urlParams.get('query'), [location.search]) || '';
  const searchOpen = useMemo(() => urlParams.get('searchOpen') === 'true', [location.search]) || false;
  const sort = useMemo(() => urlParams.get('sort') || SortType.desc, [location.search]) || '';
  const tabIndex = useMemo(() => Number(urlParams.get('tabIndex')), [location.search]) || 0;
  const status = useMemo(() => urlParams.getAll('status'), [location.search]) || [];
  const merchants = useMemo(() => urlParams.getAll('merchant'), [location.search]) || [];
  const startDate = useMemo(() => urlParams.get('startDate') || moment().subtract(3, 'months').format('YYYY-MM-DD'), [location.search]);
  const endDate = useMemo(() => urlParams.get('endDate') || moment().format('YYYY-MM-DD'), [location.search]);
  const startCollectionDate = useMemo(() => urlParams.get('startCollectionDate'), [location.search]) || '';
  const endCollectionDate = useMemo(() => urlParams.get('endCollectionDate'), [location.search]) || '';
  const conciergeOnly = useMemo(() => urlParams.get('conciergeOnly'), [location.search]) || false;

  const ListingFilters: any = {
    "Create Date": [startDate ?? undefined, endDate ?? undefined].filter(o => o),
    "Collection Date": [startCollectionDate ?? undefined, endCollectionDate ?? undefined].filter(o => o),
    "Order Status": status,
    "Collection Name": merchants,
    "Only Concierge Orders": conciergeOnly
  }

  // If Delivery Orders feature not toggled on
  // for organization
  if (!userData.isCollectEnabled && userData.role !== "Admin") {
    window.location.href = "/";
  }

  const setParam = (key: string, value: any) => {
    urlParams.delete(key);
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          urlParams.append(key, v);
        });
      } else {
        urlParams.set(key, value);
      }
      history.push({ pathname: location.pathname, search: urlParams.toString() });
    }
  }

  const deleteParam = (key: string) => {
    urlParams.delete(key);
    history.push({ pathname: location.pathname, search: urlParams.toString() });
  }

  const setPage = (p: number) => {
    setParam('page', p);
  }

  useEffect(() => {
    getDeliveryStatusTypes(
      (res) => {
        setFilterOptions((prev) => ({
          ...prev,
          // eslint-disable-next-line no-useless-computed-key
          ["Order Status"]: res.map((i) => ({
            title: i.Name,
            value: i.Code,
          })),
        }));
      },
      () => { }
    );

    // Admin only feature
    if (isTKAdmin) {
      getMerchantFilters(
        (res) => {
          setFilterOptions((prev: any) => ({
            ...prev,
            // eslint-disable-next-line no-useless-computed-key
            ["Collection Name"]: res.map((i) => ({
              title: i.MerchantName,
              value: i.MerchantName,
            })),
          }));
        },
        () => { }
      );
    }
  }, [isTKAdmin]);

  const getCollectOrders = useCallback(
    (size = pageSize, currentPage = 1) => {
      const url = getCollectOrderTabUrl(tabIndex);
      const filters: any = {}
      if (startDate) {
        filters.StartDate = new Date(startDate).toISOString();
      }
      if (endDate) {
        filters.EndDate = new Date(endDate).toISOString();
      }
      if (startCollectionDate) {
        filters.StartCollectionDate = new Date(startCollectionDate).toISOString();
      }
      if (endCollectionDate) {
        filters.EndCollectionDate = new Date(endCollectionDate).toISOString();
      }
      if (status?.length > 0) {
        filters.Status = status;
      }
      if (merchants.length > 0) {
        filters.Merchants = merchants;
      }
      if (conciergeOnly) {
        filters.ConciergeOnly = true;
      }

      dispatch(
        GetCollectionOrderList(url, {
          Order: sort,
          Page: currentPage,
          Size: size,
          Query: query,
          Filter: filters
        })
      );
    },
    [tabIndex, query, sort, startDate, endDate,
      startCollectionDate, endCollectionDate,
      conciergeOnly, page, status.length, merchants.length]
  );

  useEffect(() => {
    setPage(1);
  }, []);

  useEffect(() => {
    dispatch(clearCollectionList());
    getCollectOrders();
  }, [tabIndex, query, sort, startDate, endDate,
    startCollectionDate, endCollectionDate,
    conciergeOnly, status.length, merchants.length]);

  const loadMore = () => {
    getCollectOrders(pageSize, page + 1);
    setPage(page + 1);
  };

  const onFilterSubmit = (submittedFilter: IFilter) => {
    setPage(1);
    deleteParam('from');
    deleteParam('conciergeOnly');
    if (submittedFilter['Create Date']) {
      setParam('startDate', submittedFilter['Create Date'][0]);
      setParam('endDate', submittedFilter['Create Date'][1]);
    }
    if (submittedFilter['Collection Date']) {
      setParam('startCollectionDate', submittedFilter['Collection Date'][0]);
      setParam('endCollectionDate', submittedFilter['Collection Date'][1]);
    }
    if (submittedFilter['Order Status']) {
      setParam('status', submittedFilter['Order Status']);
    }
    if (submittedFilter['Collection Name']) {
      setParam('merchant', submittedFilter['Collection Name']);
    }

    const hasConciergeFilter = submittedFilter['Only Concierge Orders'] && submittedFilter['Only Concierge Orders'].length === 1 && Boolean(submittedFilter['Only Concierge Orders']);
    if (hasConciergeFilter) {
      setParam('conciergeOnly', true);
    }
  };

  const onTabChange = (_: number, newValue: number) => {
    if (newValue !== tabIndex) {
      dispatch(clearCollectionList());
      setParam('tabIndex', newValue);
      setPage(1);
    }
  };

  const handleClickNew = () => {
    window.location.href = `/new-delivery-order`;
  };

  const onCancel = () => {
    setPage(1);
    setParam('searchOpen', false);
    deleteParam('query');
  };

  const getTabText = useCallback(() => {
    const text = "You don’t have any";
    switch (tabIndex) {
      case 0:
        return `${text} orders`;
      case 1:
        return `${text} out for delivery orders`;
      case 2:
        return `${text} archived orders`;
      default:
        return `${text} orders`;
    }
  }, [tabIndex]);

  const getTabSource = (idx: number) => {
    switch (idx) {
      case 0:
        return ICollectOrderItemSource.Open;
      case 1:
        return ICollectOrderItemSource.OutForDelivery;
      case 2:
        return ICollectOrderItemSource.Failed;
      case 3:
        return ICollectOrderItemSource.Archived;
      default:
        return ICollectOrderItemSource.Open;
    }
  };

  const renderTabs = () => {
    return (
      <Container className={classes.tabContainer}>
        <CustomTabs value={tabIndex} onChange={onTabChange}>
          {DELIVERY_ORDERS_TABS.map((tab: string) => (
            <CustomTab key={tab} label={tab} />
          ))}
        </CustomTabs>
      </Container>
    );
  };

  const renderNewBtn = (style?: React.CSSProperties) => {
    if (userData.role === "Admin" || userData.role === "Merchant") {
      return null;
    }

    return (
      <ButtonPrimary
        className={classes.addNewBtn}
        onClick={handleClickNew}
        startIcon={<Add />}
        style={style}
      >
        New Delivery
      </ButtonPrimary>
    );
  };

  const renderHeader = () => {
    return (
      <HeaderWithButtonsResponsive title="Delivery Orders">
        <ButtonAreaResponsive>
          <div className={classes.buttonSet}>
            <SortButton
              data-automation="sort-button"
              menuItems={ASC_SORT.map(({ title, value }: SortOption) => ({
                title,
                value,
                handleSelect: () => {
                  dispatch(clearCollectionList());
                  setPage(1);
                  setParam('sort', value);
                },
              }))}
              selectedSortType={sort}
            />
            <FilterButtonWithDate
              defaultOption="Create Date"
              dateKey={["Create Date", "Collection Date"]}
              data-automation="filter-button"
              onSubmit={onFilterSubmit}
              options={filterOptions}
              filter={ListingFilters}
              singleDate
            />
            <IconButton
              data-automation="search-button"
              className={classes.searchIconButton}
              color="primary"
              onClick={() => setParam('searchOpen', true)}>
              <SearchRoundedIcon
                color="primary"
                className={classes.searchIcon}
              />
              {mobileView && (
                <Typography variant="button" component="span" color="primary">
                  Search
                </Typography>
              )}
            </IconButton>
            {renderNewBtn()}
          </div>
        </ButtonAreaResponsive>
      </HeaderWithButtonsResponsive>
    );
  };

  const renderSearchbar = () => {
    return (
      <Searchbar
        query={query}
        isOpen={searchOpen}
        placeholder="Search Delivery Orders"
        onCancel={onCancel}
        onChange={(e) => {
          setPage(1);
          if (e.target.value.length > 0) {
            const debouncedFunc = debounce((_e) => {
              setParam('query', _e.target.value);
            }, DEBOUNCE_TIMEOUT);

            debouncedFunc(e);
          } else {
            deleteParam('query');
          }
        }}
      />
    );
  };

  const renderNoOrders = () => {
    return (
      <Grid
        container
        item
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: "96px" }}
      >
        <Grid
          container
          item
          justifyContent="center"
          alignItems="center"
          style={{ maxWidth: "320px" }}
        >
          <Typography variant="h6" component="span" align="center">
            {getTabText()}
          </Typography>
          {tabIndex === 0 &&
            (userData.role === "Admin" || userData.role === "Merchant" ? (
              <></>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="span"
                  align="center"
                  style={{ marginTop: "12px" }}
                >
                  To get started, create a new Collection order
                </Typography>

                {renderNewBtn({ marginTop: 32 })}
              </>
            ))}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <CssBaseline />
      <Container className={classes.root}>
        <CssBaseline />
        {renderHeader()}
        {renderSearchbar()}

        {renderTabs()}
        {loading && page === 1 ? (
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: "96px" }}
          >
            <CircularProgress />
          </Grid>
        ) : orders && orders?.length > 0 ? (
          <CollectionOrdersList
            data={orders}
            totalCount={totalCount}
            source={getTabSource(tabIndex)}
            loadMore={loadMore}
          />
        ) : query ? (
          <Grid
            container
            item
            className={classes.uPaddingLeft}
            style={{ marginTop: "40px" }}
          >
            <Grid container item style={{ maxWidth: "320px" }}>
              <Typography variant="h6" component="span">
                0 search results found
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                component="span"
                style={{ marginTop: "12px" }}
              >
                Adjust your search term and try again
              </Typography>
            </Grid>
          </Grid>
        ) : Object.keys(ListingFilters).some((key) => {
          return ListingFilters[key]?.length !== 0;
        }) ? (
          <Grid
            container
            item
            className={classes.uPaddingLeft}
            style={{ marginTop: "40px" }}
          >
            <Grid container item style={{ maxWidth: "320px" }}>
              <Typography variant="h6" component="span">
                0 results found
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                component="span"
                style={{ marginTop: "12px" }}
              >
                Adjust your filtering and try again
              </Typography>
            </Grid>
          </Grid>
        ) : (
          renderNoOrders()
        )}
      </Container>
    </Box>
  );
}

export default CollectionOrders;
